// Focus for a modal dialog drawn in the page itself (the clinic page has no portals), as the
// WAI-ARIA modal dialog pattern has it:
// - focus moves into the dialog as it opens (dialogRef's element, which takes tabIndex -1);
// - Tab and Shift+Tab go round inside it;
// - Escape closes it wherever the focus is, even after a click outside it. It is heard on the
//   window before anything else, and goes no further, so the page's own Escape (closing an
//   expanded report preview) doesn't act on it too;
// - once it closes, focus goes back to what had it before it opened, if that is still there.
// MembersDialog and NotesDialog use it.
import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export default function useDialogFocus(dialogRef, onClose) {
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement;
    dialogRef.current?.focus();

    const onKeyDown = (event) => {
      const dialog = dialogRef.current;
      if (!dialog || event.isComposing) return; // a key that ends an IME's composition is the IME's
      if (event.key === 'Escape') {
        event.stopPropagation();
        close.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const stops = Array.from(dialog.querySelectorAll(FOCUSABLE));
      const active = document.activeElement;
      if (stops.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const inside = dialog.contains(active);
      if (event.shiftKey && (!inside || active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      if (opener && opener !== document.body && opener.isConnected && typeof opener.focus === 'function') opener.focus();
    };
  }, [dialogRef]);
}
